import { useQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';

export interface ChartItem {
  label: string;
  value: number;
  color: string;
}

interface ChartCategory {
  category: string;
  balance: string;
}

interface AssetsChart {
  balance: number;
  categories: ChartCategory[];
}

interface LiabilitiesChart {
  balance: number;
  categories: ChartCategory[];
}

interface AssetDetail {
  id: string;
  owner: string;
  name: string;
  balance: number | null;
}

interface AssetGroup {
  assetGroup: string;
  assetGroupBalance: number;
  assetDetails: AssetDetail[];
}

interface Assets {
  assetsBalance: number;
  assetGroups: AssetGroup[];
}

interface LiabilityDetail {
  id: string;
  owner: string;
  name: string;
  balance: number;
}

interface LiabilityGroup {
  liabilityGroup: string;
  liabilityGroupBalance: number;
  liabilityDetails: LiabilityDetail[];
}

interface Liabilities {
  liabilitiesBalance: number;
  liabilityGroups: LiabilityGroup[];
}

export interface AssetsSectionDTO {
  label?: string;
  total: number;
  chart: ChartItem[];
}

export interface LiabilitiesSectionDTO {
  total: number;
  chart: ChartItem[];
}

export enum RowType {
  Group = 'group',
  Detail = 'detail',
  Total = 'total',
}

interface TableRow {
  label: string;
  value: number[];
  color: string | null;
  rowType: RowType;
}

export interface TableSectionDTO {
  headers: string[];
  rows: TableRow[];
}

export interface EstateAssetsSection {
  assets: AssetsSectionDTO;
  liabilities: LiabilitiesSectionDTO;
  table: TableSectionDTO;
}

export interface OutOfEstateAssetsSection {
  assets: AssetsSectionDTO;
  table: TableSectionDTO;
}

export interface SummarySectionDTO {
  netWorth: number;
  outOfEstateAssetsValue: number | null;
  total: number | null;
  inEstateAssets: EstateAssetsSection;
  outOfEstateAssets: OutOfEstateAssetsSection | null;
}

interface ChartData {
  assetsChart: AssetsChart;
  liabilitiesChart: LiabilitiesChart;
}

interface BalanceSheetEntry {
  owner: string;
  assets: Assets;
  liabilities: Liabilities;
  networth: number;
}

interface OwnerDTO {
  name: string;
  type: string;
  color: string | null;
  valueLabel: string;
  value: number;
  percentage: number;
}

export interface InEstateSummaryDTO {
  assets: AssetsSectionDTO;
  liabilities: AssetsSectionDTO;
  owners: OwnerDTO[];
  table: TableSectionDTO;
}

export type OutOfEstateSummaryDTO = Omit<InEstateSummaryDTO, 'liabilities' | 'owners'> | null;

export interface EntityLevelSummariesDTO {
  assets: AssetsSectionDTO;
  owners: OwnerDTO[];
  table: TableSectionDTO;
  chartLabel: string;
}

export interface BalanceSheetDTO {
  OwnerOptions: string[];
  ChartData: ChartData;
  BalanceSheet: BalanceSheetEntry[];
  Summary: SummarySectionDTO;
  InEstateSummary: InEstateSummaryDTO;
  OutOfEstateSummary: OutOfEstateSummaryDTO;
  EntityLevelSummaries: EntityLevelSummariesDTO[];
}

interface FetchParams {
  clientId: number;
  owners: string[] | null;
}

export const fetchBalanceSheet = async ({
  clientId,
  owners,
}: FetchParams): Promise<BalanceSheetDTO> => {
  return authFetch(urlWithParams('/Consumer/BalanceSheetView', { clientId, owners }));
};

interface Params {
  clientId: number;
  owners?: string[] | null;
  onSuccess?: (response: BalanceSheetDTO) => void;
  onError?(): void;
}

export const useBalanceSheetQuery = ({
  clientId,
  onSuccess = () => {},
  onError = () => {},
  owners = null,
}: Params) => {
  return useQuery(
    queryKeys.balanceSheet(clientId, owners),

    () => fetchBalanceSheet({ clientId, owners }),

    {
      onSuccess,
      onError,
    },
  );
};
